import { useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import cc from 'classnames';

import Layout from '@/components/Layout/Vanilla';
import Button from '@/components/Button';
import Slider from '@/components/Slider';
import { DefaultLogoReel } from '@/components/LogoReel';
import Ratings from '@/components/Ratings';
import SliderTestimonial from '@/components/SliderTestimonial';
import Hero from '@/components/sections/home/hero';
import Logos from '@/components/sections/home/Logos';
import USPCardRedesigned from '@/components/USPCardRedesigned';
import HomeSectionHero from '@/components/sections/home/HomeSectionHero';
import CreatorsTabs from '@/components/sections/home/CreatorsTabs';
import BusinessesTabs from '@/components/sections/home/BusinessesTabs';
import EngineeringTabs from '@/components/sections/home/EngineeringTabs';
import { scrollTo } from '@/lib/scroll';
import { fetchAPI, gql } from '@/lib/graphcms';

import VercelSVG from '@/svg/icons/vercel.svg';
import ZapierSVG from '@/svg/icons/zapier.svg';
import JiraSVG from '@/svg/icons/jira.svg';
import GatsbyCloudSVG from '@/svg/icons/gatsby-cloud.svg';
import SlackColourSVG from '@/svg/icons/slack-colour.svg';
import SlackSVG from '@/svg/icons/slack.svg';
import ShopifySVG from '@/svg/icons/shopify.svg';
import BynderSVG from '@/svg/icons/bynder.svg';
import AsanaSVG from '@/svg/icons/asana.svg';
import NetlifySVG from '@/svg/icons/netlify.svg';
import WebhooksSVG from '@/svg/icons/webhooks-2.svg';
import GoogleDriveSVG from '@/svg/icons/google-drive.svg';
import SpotifySVG from '@/svg/icons/spotify.svg';
import ArrowSVG from '@/svg/icons/arrow.svg';

import {
  Adobe,
  AWS,
  Salesforce,
  Oracle,
  SAP,
  Segment,
  GoogleAnalytics,
  Zapier,
  DynamicYield,
  Hubspot,
  ActiveCampaign,
  Slack,
  ReactJS,
  NextJS,
  Heroku,
  Vercel,
  Postman,
  Shopify,
  Commercetools,
  ServiceNow,
  Atlassian,
} from '@/components/icons';
import { Businesses, Creators, Engineering } from '@/components/icons/personas';

const cloudServices = [
  {
    animationClass: 'tick-1',
    nudge: true,
    group: [
      {
        icon: <VercelSVG />,
        name: 'Vercel',
        description:
          'Build and preview your sites with the native Vercel integration',
        hex: '#000000',
      },
      {
        icon: <ZapierSVG />,
        name: 'Zapier',
        description: 'Trigger custom zaps and workflows with webhooks',
        hex: '#FF4A00',
      },
      {
        icon: <JiraSVG />,
        name: 'JIRA',
        description: 'Incorporate content changes into your workflow',
        hex: '#0052CC',
      },
      {
        icon: <GatsbyCloudSVG />,
        name: 'Gatsby Cloud',
        description: `Build and preview your sites with the native Gatsby Cloud Integration`,
        hex: '#663399',
      },
    ],
  },
  {
    animationClass: 'tick-2',
    group: [
      {
        icon: <SlackColourSVG />,
        name: 'Slack',
        description:
          'Configure notifications in Slack when content changes are made',
        hex: '#451B4A',
      },
      {
        icon: <ShopifySVG />,
        name: 'Shopify',
        description: 'Display all your products and variations within the GUI',
        hex: '#002E25',
      },
      {
        icon: <BynderSVG />,
        name: 'Bynder',
        description:
          'Query all your assets from Bynder and serve them via GraphCMS',
        hex: '#00AAFF',
      },
      {
        icon: <AsanaSVG />,
        name: 'Asana',
        description: 'Content management adapting to your preferred workflows',
        hex: '#F6F8F9',
      },
    ],
  },
  {
    animationClass: 'tick-3',
    nudge: true,
    group: [
      {
        icon: <NetlifySVG />,
        name: 'Netlify',
        description:
          'Build and preview your sites with the Netlify integration',
        hex: '#00AD9F',
      },
      {
        icon: <WebhooksSVG />,
        name: 'Webhooks',
        description: 'Create more custom integrations with granular webhooks',
        hex: '#6246EA',
      },
      {
        icon: <GoogleDriveSVG />,
        name: 'Google Drive',
        description: 'Import assets via your folders and upload to GraphCMS',
        hex: '#F8F9FB',
      },
      {
        icon: <SpotifySVG />,
        name: 'Spotify',
        description: 'Send an RSS feed of all your podcast episodes to Spotify',
        hex: '#1ED760',
      },
    ],
  },
];

const forEngineeringSectionLogos = [
  {
    icon: <ReactJS />,
    id: 'react',
  },
  {
    icon: <NextJS />,
    id: 'nextjs',
  },
  {
    icon: <Heroku />,
    id: 'heroku',
  },
  {
    icon: <Vercel />,
    id: 'vercel',
  },
  {
    icon: <Postman />,
    id: 'postman',
  },
  {
    icon: <AWS />,
    id: 'aws',
  },
  {
    icon: <Shopify />,
    id: 'shopify',
  },
];

const forCreatorsSectionLogos = [
  {
    icon: <Segment />,
    id: 'segment',
  },
  {
    icon: <GoogleAnalytics />,
    id: 'ga',
  },
  {
    icon: <Zapier />,
    id: 'zapier',
  },
  {
    icon: <DynamicYield />,
    id: 'dy',
  },
  {
    icon: <Hubspot />,
    id: 'hubspot',
  },
  {
    icon: <ActiveCampaign />,
    id: 'ac',
  },
  {
    icon: <Slack />,
    id: 'slack',
  },
];

const forBusinessesSectionLogos = [
  {
    icon: <SAP />,
    id: 'sap',
  },
  {
    icon: <Commercetools />,
    id: 'commercetools',
  },
  {
    icon: <Salesforce />,
    id: 'salesforce',
  },
  {
    icon: <Adobe />,
    id: 'adobe',
  },
  {
    icon: <Oracle />,
    id: 'oracle',
  },
  {
    icon: <ServiceNow />,
    id: 'service',
  },
  {
    icon: <Atlassian />,
    id: 'atlassian',
  },
];

export default function Home({ page, ratings, testimonials, preview }) {
  const refEng = useRef(null);
  const refCreators = useRef(null);
  const refBusinesses = useRef(null);

  const personas = [
    {
      icon: <Engineering className="text-cornflower" />,
      title: 'For Engineering Teams',
      description:
        'GraphCMS is the ultimate content platform that enables you to deliver ambitious applications at scale, API first, using your preferred frameworks.',
      theme: 'light',
      anchorTo: () => scrollTo(refEng),
      anchor: 'GraphCMS for Engineering',
    },
    {
      icon: <Creators className="text-rose" />,
      title: 'For Content Creators',
      description:
        'GraphCMS empowers content creators, editors, and marketing teams to create exceptional digital experiences in the way they’ve defined.',
      theme: 'pink',
      anchorTo: () => scrollTo(refCreators),
      anchor: 'GraphCMS for Creators',
    },
    {
      icon: <Businesses className="text-strong-blue" />,
      title: 'For Businesses',
      description:
        'Inflexible technologies keep your business from innovating. Integrate all your existing services to deliver content from a single source.',
      theme: 'royal',
      anchorTo: () => scrollTo(refBusinesses),
      anchor: 'GraphCMS for Businesses',
    },
  ];

  return (
    <Layout seo={page} preview={preview}>
      <div className="h-full overflow-hidden sm:pt-12 lg:relative lg:py-24">
        <Hero page={page} />
      </div>

      <DefaultLogoReel className="mt-12 md:mb-24 lg:mt-28" />

      <section className="bg-white py-12 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mx-auto max-w-4xl">
            <div className="mx-auto w-full space-y-6 text-center">
              <h2 className="text-3xl font-bold leading-tight text-haiti md:text-5xl">
                Empowering Teams With Better Content Management
              </h2>
              <div className="mx-auto max-w-3xl">
                <p className="text-zodiac">
                  GraphCMS is the Headless CMS allowing you to build digital
                  experiences the way you envisioned them - with all your
                  backends, frontends, and services, working together in
                  harmony.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-x-6 pt-12 md:grid-cols-2 md:pb-6 lg:grid-cols-3 lg:pt-20">
            {personas.map((persona) => (
              <USPCardRedesigned key={persona.title} {...persona} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-haiti py-12 lg:py-30" ref={refEng}>
        <div className="mx-auto max-w-6xl px-6">
          <EngineeringTabs />

          <Logos
            title="Works with all your preferred frameworks, backends, and headless APIs"
            className="text-white"
            logos={forEngineeringSectionLogos}
          />
        </div>
      </section>

      <section className="bg-whisper py-12 lg:py-30" ref={refCreators}>
        <div className="mx-auto max-w-6xl px-6">
          <CreatorsTabs />

          <Logos
            title="Don’t give up working with your favorite tools and workflows"
            className="text-haiti"
            logos={forCreatorsSectionLogos}
          />
        </div>
      </section>

      <section className="bg-white py-12 lg:py-30" ref={refBusinesses}>
        <div className="mx-auto max-w-6xl px-6">
          <BusinessesTabs />

          <Logos
            title="Reduce time to market by unifying your stack"
            className="text-haiti"
            logos={forBusinessesSectionLogos}
          />
        </div>
      </section>

      <section className="bg-whisper py-12 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="md:flex md:items-center">
            <div className="md:w-2/5 md:flex-shrink-0">
              <div className="space-y-6 lg:pr-6">
                <HomeSectionHero
                  title="Start Building With GraphCMS For Free"
                  description="Create a free-forever account today, and begin unifying, structuring, enriching, and distributing your content across devices."
                />
                <Button
                  href="https://app.graphcms.com/signup"
                  colour="purple"
                  size="large"
                >
                  Get Started &#8594;
                </Button>
              </div>
            </div>
            <div className="relative -my-6 -mx-6 mt-12 overflow-hidden md:my-0 md:mx-0 md:-mr-96 md:w-4/5 md:py-20">
              <div className="ticker absolute left-0 top-0 z-20 hidden h-full w-full md:block" />
              {cloudServices.map(({ nudge, animationClass, group }, index) => (
                <div
                  key={index}
                  className={cc([
                    'flex-nowrap',
                    {
                      '-ml-12 hidden md:flex': nudge,
                      flex: !nudge,
                    },
                  ])}
                >
                  <div
                    className={cc([
                      'flex items-center justify-center transition md:flex-nowrap',
                      animationClass,
                    ])}
                  >
                    {group.map((service) => (
                      <div className="flex w-84 p-6" key={service.name}>
                        <div className="w-full rounded bg-white px-4 pb-4 shadow-sm">
                          <div className="flex items-end pb-3">
                            <div
                              style={{ backgroundColor: service.hex }}
                              className="mr-3 -mt-4 flex h-12 w-12 items-center justify-center rounded-sm"
                            >
                              {service.icon}
                            </div>
                            <p className="font-medium text-haiti">
                              {service.name}
                            </p>
                          </div>
                          <p className="text-sm text-light-haiti">
                            {service.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className={cc([
                      'flex flex-nowrap items-center justify-center transition',
                      animationClass,
                    ])}
                  >
                    {group.map((service) => (
                      <div className="flex w-84 p-6" key={service.name}>
                        <div className="w-full rounded bg-white px-4 pb-4 shadow-sm">
                          <div className="flex items-end pb-3">
                            <div
                              style={{ backgroundColor: service.hex }}
                              className="mr-3 -mt-4 flex h-12 w-12 items-center justify-center rounded-sm"
                            >
                              {service.icon}
                            </div>
                            <p className="font-medium text-haiti">
                              {service.name}
                            </p>
                          </div>
                          <p className="text-sm text-light-haiti">
                            {service.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-haiti">
        <div className="mx-auto max-w-6xl px-6">
          <div className="lg:flex">
            <div className="relative z-20 py-12 md:py-30 lg:w-1/2">
              <div className="space-y-8 lg:space-y-12">
                <div className="space-y-6 lg:pr-6">
                  <h2 className="text-3xl font-bold leading-snug text-white lg:text-5xl">
                    Content
                    <br className="hidden lg:block" /> Management That
                    <br className="hidden lg:block" /> Scales With You
                  </h2>
                  <p className="text-lg text-white">
                    With industry leading performance, security, and
                    scalability, GraphCMS is trusted by teams ranging from the
                    Fortune 500, to governments and public services.
                  </p>
                </div>
                <div className="flex flex-wrap md:flex-nowrap md:space-x-8">
                  <div className="w-1/2 md:w-auto md:flex-initial">
                    <p className="text-sm text-white">
                      <span className="block text-2xl font-black uppercase md:text-3xl">
                        1B+/m
                      </span>
                      API Requests
                    </p>
                  </div>
                  <div className="w-1/2 md:w-auto md:flex-initial">
                    <p className="text-sm text-white">
                      <span className="block text-2xl font-black uppercase md:text-3xl">
                        40k+
                      </span>
                      Active Teams
                    </p>
                  </div>
                  <div className="mt-6 w-1/2 md:mt-0 md:w-auto md:flex-initial">
                    <p className="text-sm text-white">
                      <span className="block text-2xl font-black uppercase md:text-3xl">
                        {'<100ms'}
                      </span>
                      Average Latency
                    </p>
                  </div>
                  <div className="mt-6 w-1/2 md:mt-0 md:w-auto md:flex-initial">
                    <p className="text-sm text-white">
                      <span className="block text-2xl font-black uppercase md:text-3xl">
                        99.99%
                      </span>
                      Average API Uptime
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className=" relative z-10 lg:w-1/2">
              <div className="absolute -mt-64 lg:right-0 lg:top-0 lg:-mt-32 lg:-mr-96">
                <Image
                  src="/images/home/globe.svg"
                  width={950}
                  height={883}
                  alt="Globe"
                  className="bg-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-12 lg:py-30">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-3xl font-bold leading-snug text-haiti md:text-4xl lg:text-center">
            Delivering Millions Of Digital Experiences For Over 50,000 Teams
            Globally
          </h2>

          <div className="my-10 mx-auto w-full px-4">
            <Ratings ratings={ratings} />
          </div>

          <div className="mx-auto w-full max-w-3xl pt-10">
            <Slider items={testimonials} Component={SliderTestimonial} />
          </div>
        </div>
      </section>

      <section className="bg-whisper py-12 lg:py-30">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-3xl font-bold leading-snug text-haiti md:text-4xl lg:text-center">
            Get A Quickstart On Building Better Digital Experiences
          </h2>

          <div className="mt-8 flex flex-col justify-between md:flex-row lg:mt-20">
            <div className="relative h-72 rounded-lg bg-haiti bg-cover bg-no-repeat shadow-image transition-all duration-300 ease-in-out hover:shadow-xl md:h-auto lg:w-full lg:max-w-sm">
              <div className="absolute h-full w-full overflow-hidden rounded-lg">
                <Image
                  alt="Woman thinking with a blue background overlay in the image"
                  src="/images/home/report-image-home.png"
                  layout="fill"
                  objectFit="cover"
                  quality={100}
                />
              </div>

              <Link href="/resources/dxp-report-2020" prefetch={false}>
                <a className="relative flex h-full flex-col p-6">
                  <p className="text-sm font-medium uppercase tracking-wide text-whisper">
                    Report
                  </p>
                  <div className="mt-auto">
                    <h3 className="mb-4 text-xl font-bold leading-6 text-white">
                      Modernizing enterprise tech stacks by embracing modular
                      DXPs
                    </h3>
                    <p className="flex items-center font-medium text-white">
                      <span className="mr-2 block">Download the report</span>
                      <ArrowSVG className="h-5 w-5 rotate-180 transform fill-current" />
                    </p>
                  </div>
                </a>
              </Link>
            </div>

            <div className="ml-0 mt-6 md:ml-6 md:mt-0">
              <div className="rounded-lg bg-white shadow-image transition-all duration-300 ease-in-out hover:shadow-card-hover">
                <a
                  href="https://www.youtube.com/watch?v=H3iEXqjYRkE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full py-6 px-4 lg:p-6"
                >
                  <p className="text-sm font-medium uppercase tracking-wide text-rose">
                    Webinar
                  </p>
                  <h3 className="mt-4 mb-2 text-xl font-bold leading-6 text-haiti md:mt-8">
                    High performance & personalization for eCommerce experiences
                    <ArrowSVG className="ml-2 inline-block h-5 w-5 rotate-180 transform fill-current" />
                  </h3>
                  <p className="text-sm leading-normal text-light-haiti">
                    Learn more about why you shouldn&apos;t have to compromise
                    when you want to deliver modern, high-performance eCommerce
                    experiences, alongside personalization and AB Testing.
                  </p>
                </a>
              </div>
              <div className="relative mt-6 overflow-hidden rounded-lg bg-white shadow-image transition-all duration-300 ease-in-out hover:shadow-card-hover">
                <a
                  href="https://slack.graphcms.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full py-6 px-4 lg:p-6"
                >
                  <p className="text-sm font-medium uppercase tracking-wide text-rose">
                    Community
                  </p>
                  <h3 className="relative z-10 mb-2 mt-4 text-xl font-bold leading-6 text-haiti md:mt-16">
                    Join our engaged community on Slack
                    <ArrowSVG className="ml-2 inline-block h-5 w-5 rotate-180 transform fill-current" />
                  </h3>
                  <p className="text-sm leading-normal text-light-haiti">
                    Connect with an engaged community of 7,000+ developers and
                    creators to get your questions resolved and learn about new
                    opportunities in the GraphQL space.
                  </p>

                  <SlackSVG className="absolute top-0 right-0 z-0 -mt-2 -mr-2 h-24 w-24 text-catskill-white" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page, ratings, testimonials } =
    (await fetchAPI(
      gql`
        fragment seoData on Seo {
          changefreq
          description
          image {
            url
            width
            height
            mimeType
          }
          keywords
          noindex
          priority
          title
        }

        fragment pageData on Page {
          id
          slug
          title
          subTitle
          coverImage {
            id
            url(
              transformation: {
                image: { resize: { width: 1724, height: 1078, fit: clip } }
              }
            )
            handle
            width
            height
            mimeType
            fileName
            alt
          }
          content
          meta: seo {
            ...seoData
          }
        }

        query homePageQuery {
          page(where: { slug: "home" }) {
            ...pageData
          }
          ratings {
            id
            platform
            url
            value
          }
          testimonials {
            company {
              caseStudy {
                slug
              }
              name
              url
              logo {
                id
                handle
                width
                height
                url(
                  transformation: {
                    image: { resize: { width: 150, fit: clip } }
                  }
                )
                alt
              }
            }
            author {
              name
              title
              twitterHandle
              bio
              photo {
                id
                handle
                width
                height
                url(
                  transformation: {
                    image: { resize: { width: 50, height: 50, fit: clip } }
                  }
                )
                alt
              }
            }
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  return {
    props: {
      preview,
      page,
      ratings,
      testimonials,
    },
    revalidate: 3600,
  };
};
